// Support Coordination participant statement: a branded A4 PDF written for the participant and
// their family. Page 1 carries the company letterhead, who the statement is for, four headline
// figures and each budget with a bar and a status word. Then every support delivered, grouped by
// month with subtotals and a grand total. Every page has "Page X of Y" and the company's contact
// line; pages 2+ repeat a slim header and the table's column headings.
//
// Data: scStatementData in backend/routes/supportCoordination.js (the participant file's
// "Statement PDF" and the participant portal). `branding` is the org letterhead from
// utils/exportBranding; anything it lacks (the portal has no staff session to fetch it) comes from
// data.provider, the same org's details sent with the statement.
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { brandRgb, pdfLogo } from './exportBranding';

// ── Page (mm, A4 portrait) ────────────────────────────────────────────────────
const PW = 210;
const PH = 297;
const M = 16;                     // side margins
const CW = PW - 2 * M;            // content width
const CONT_TOP = 24;              // body top on pages 2+, under the running header
const FOOT_RULE = 279;            // footer rule on every page
const BODY_END = FOOT_RULE - 6;   // the body never runs below this
const PAD = 2.2;                  // table cell padding, top and bottom
const PAD_X = 2.5;                // table cell padding, left and right
const HEAD_H = 8.7;               // a table's column-heading row
const HEADING_H = 8.5;            // a section heading
const BAND = 9;                   // month band drawn above the first support of each month
const SUB_H = 7.6;                // month subtotal drawn under the last support of each month
const TOTAL_H = 11;               // grand total drawn under the very last support
const SUP_COLS = [22, 80, 31, 16, 29];          // Date, What was done, Time, Hours, Amount
const BUDGET_COLS = [55, 24, 24, 25, 28, 22];   // Budget, Amount, Used, Remaining, Progress, Status

// ── Colour ────────────────────────────────────────────────────────────────────
const INK = [15, 23, 42];         // slate-900
const TEXT = [51, 65, 85];        // slate-700
const MUTED = [100, 116, 139];    // slate-500: the lightest grey used for words (4.8:1 on white)
const RULE = [226, 232, 240];     // slate-200
const PANEL = [248, 250, 252];    // slate-50
const WHITE = [255, 255, 255];
const BLACK = [0, 0, 0];
const VIOLET = '7C3AED';          // Support Coordination's hue, used when the company has no brand colour
const STATUS = {
  ok: { word: 'On track', ink: [4, 120, 87], bar: [16, 185, 129], tint: [236, 253, 245] },
  low: { word: 'Running low', ink: [180, 83, 9], bar: [245, 158, 11], tint: [255, 247, 237] },
  used: { word: 'Fully used', ink: [180, 83, 9], bar: [245, 158, 11], tint: [255, 247, 237] },
  over: { word: 'Over budget', ink: [185, 28, 28], bar: [220, 38, 38], tint: [254, 242, 242] },
  none: { word: 'Not set', ink: MUTED, bar: [203, 213, 225], tint: [241, 245, 249] },
};
const LOW_SHARE = 0.15;           // "running low" = under 15% left, the same line as the SC dashboard

// ── Words ─────────────────────────────────────────────────────────────────────
const LEVELS = {
  support_connection: 'Support connection (level 1)',
  support_coordination: 'Support coordination (level 2)',
  specialist: 'Specialist support coordination (level 3)',
};
const CLAIM_WORDS = {
  NF2F: 'Phone, email or report work', THLT: 'Telehealth', TRAN: 'Travel',
  REPW: 'Report requested by the NDIA', CANC: 'Short-notice cancellation',
};
const CONTACT_WORDS = { phone: 'Phone call', email: 'Email', f2f: 'Face-to-face meeting', admin: 'Administration' };
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const NOTE_MAX = 300;             // a statement shows what was done, not the whole case note

// ── Text ──────────────────────────────────────────────────────────────────────
// The PDF's built-in Helvetica only carries Windows-1252. Any other character (an arrow, an emoji,
// a Vietnamese tone mark) turns the WHOLE line into spaced-out junk, so map or drop them first.
const CP1252 = '€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ';
const printable = (ch) => {
  const c = ch.codePointAt(0);
  return (c >= 32 && c < 127) || (c > 160 && c <= 255) || CP1252.includes(ch);
};
function safe(v) {
  if (v == null) return '';
  return Array.from(String(v).replace(/\s+/g, ' ')).map((ch) => {
    if (printable(ch)) return ch;
    const c = ch.codePointAt(0);
    if ((c >= 0x2010 && c <= 0x2012) || c === 0x2212 || (c >= 0x2190 && c <= 0x21ff)) return '-';
    const bare = ch.normalize('NFD').replace(/[̀-ͯ]/g, '');
    return Array.from(bare).every(printable) ? bare : '';
  }).join('').replace(/ {2,}/g, ' ').trim();
}
const sentence = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
const plural = (n, one, many) => `${n.toLocaleString('en-AU')} ${n === 1 ? one : many}`;
function clip(s, max) {
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const space = cut.lastIndexOf(' ');
  let out = space > max * 0.6 ? cut.slice(0, space) : cut;
  while (out && ' ,;:.-'.includes(out.charAt(out.length - 1))) out = out.slice(0, -1);
  return `${out}…`;
}

// ── Numbers and dates ─────────────────────────────────────────────────────────
const num2 = (n) => (Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = (n) => `${Number(n) < 0 ? '-' : ''}$${num2(Math.abs(Number(n) || 0))}`;
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const hoursWords = (h) => { const v = round2(h); return `${v.toLocaleString('en-AU', { maximumFractionDigits: 2 })} ${v === 1 ? 'hour' : 'hours'}`; };
const sum = (list, key) => list.reduce((s, x) => s + (Number(x[key]) || 0), 0);

// Dates arrive as 'YYYY-MM-DD' (pg DATE is returned as text); read them without a time zone so a
// support on the 1st never shows as the 31st.
function parseDay(d) {
  if (!d) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d instanceof Date ? d.toISOString() : String(d));
  return m ? { y: Number(m[1]), m: Number(m[2]) - 1, d: Number(m[3]) } : null;
}
const dayLong = (s) => { const p = parseDay(s); return p ? `${p.d} ${MONTHS[p.m]} ${p.y}` : ''; };
const dayShort = (s) => { const p = parseDay(s); return p ? `${p.d} ${MONTHS[p.m].slice(0, 3)} ${p.y}` : ''; };
function weekdayDay(s) {
  const p = parseDay(s);
  if (!p) return '';
  return `${WEEKDAYS[new Date(Date.UTC(p.y, p.m, p.d)).getUTCDay()]} ${p.d} ${MONTHS[p.m].slice(0, 3)}`;
}
const monthKey = (s) => { const p = parseDay(s); return p ? `${p.y}-${String(p.m + 1).padStart(2, '0')}` : ''; };
const monthLabel = (key) => { const p = parseDay(`${key}-01`); return p ? `${MONTHS[p.m]} ${p.y}` : 'Date not recorded'; };
function todayIso() {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}
function range(from, to) {
  const f = parseDay(from);
  const t = parseDay(to);
  if (f && t) {
    const lastDay = new Date(Date.UTC(t.y, t.m + 1, 0)).getUTCDate();
    if (f.y === t.y && f.m === t.m && f.d === 1 && t.d === lastDay) {
      const month = `${MONTHS[f.m]} ${f.y}`;
      return { long: month, short: month };
    }
    const short = f.y === t.y
      ? `${f.d} ${MONTHS[f.m].slice(0, 3)} – ${dayShort(to)}`
      : `${dayShort(from)} – ${dayShort(to)}`;
    return { long: `${dayLong(from)} to ${dayLong(to)}`, short };
  }
  if (f) return { long: `From ${dayLong(from)}`, short: `From ${dayShort(from)}` };
  if (t) return { long: `Up to ${dayLong(to)}`, short: `Up to ${dayShort(to)}` };
  return null;
}
// 'HH:MM:SS' → '9:30–10:45 am' (one am/pm when both ends share it).
function clock(t) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t || ''));
  if (!m) return null;
  const h = Number(m[1]);
  return { text: `${h % 12 || 12}:${m[2]}`, ap: h < 12 ? 'am' : 'pm' };
}
function timeRange(start, end) {
  const a = clock(start);
  const b = clock(end);
  if (!a) return '';
  if (!b) return `${a.text} ${a.ap}`;
  return a.ap === b.ap ? `${a.text}–${b.text} ${b.ap}` : `${a.text} ${a.ap}–${b.text} ${b.ap}`;
}
function fundingWords(src) {
  const v = String(src == null ? '' : src).trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!v) return '';
  if (['ndia', 'ndis', 'agency', 'agency_managed', 'ndia_managed', 'ndis_managed'].includes(v)) return 'NDIA-managed';
  if (['plan', 'plan_managed', 'pm', 'plan_manager'].includes(v)) return 'Plan-managed';
  if (['self', 'self_managed', 'sm'].includes(v)) return 'Self-managed';
  return sentence(safe(v.replace(/_/g, ' ')));
}

// ── Colour helpers ────────────────────────────────────────────────────────────
function luminance(c) {
  const [r, g, b] = c.map((v) => { const s = v / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
const mix = (c, to, t) => c.map((v, i) => Math.round(v + (to[i] - v) * t));
// The brand colour darkened just enough to pass WCAG AA as text on white (and on its own tint),
// so a pale brand colour never produces unreadable headings.
function readable(c) {
  let out = c;
  for (let i = 0; i < 30 && 1.05 / (luminance(out) + 0.05) < 5; i += 1) out = mix(out, BLACK, 0.08);
  return out;
}

// ── Drawing helpers ───────────────────────────────────────────────────────────
const lh = (size) => size * 0.3528 * 1.15;  // mm per line at jsPDF's 1.15 line height
function font(doc, size, style = 'normal', color = INK) {
  doc.setFont('helvetica', style);
  doc.setFontSize(size);
  doc.setTextColor(color[0], color[1], color[2]);
}
function ellipsize(doc, s, width) {
  if (doc.getTextWidth(s) <= width) return s;
  let t = s;
  while (t.length > 1 && doc.getTextWidth(`${t}…`) > width) t = t.slice(0, -1);
  return `${t.trimEnd()}…`;
}
// Word wrap that also breaks after a hyphen, so "Fitzgerald-Montgomery" wraps between the names
// rather than mid-word (jsPDF's own splitter only knows spaces).
function wrap(doc, text, width, maxLines) {
  const parts = [];
  let gap = '';  // the spaces before the next word, kept as written ("   ·   " stays wide)
  String(text).split(/( +)/).forEach((word) => {
    if (!word) return;
    if (word.charAt(0) === ' ') { gap = word; return; }
    const bits = word.split('-');
    bits.forEach((b, bi) => {
      const piece = bi < bits.length - 1 ? `${b}-` : b;
      if (piece) parts.push({ piece, glue: bi === 0 ? gap : '' });
    });
    gap = '';
  });
  const packed = [];
  let cur = '';
  parts.forEach(({ piece, glue }) => {
    const next = cur ? cur + glue + piece : piece;
    if (!cur || doc.getTextWidth(next) <= width) { cur = next; return; }
    packed.push(cur);
    cur = piece;
  });
  if (cur) packed.push(cur);
  // A single piece wider than the column still has to break somewhere.
  const lines = packed.reduce((all, ln) => all.concat(doc.getTextWidth(ln) > width ? doc.splitTextToSize(ln, width) : [ln]), []);
  if (!lines.length) return [''];
  if (!maxLines || lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines);
  kept[maxLines - 1] = ellipsize(doc, `${kept[maxLines - 1]} ${lines[maxLines]}`, width);
  return kept;
}
// Largest font size (down to `min`) at which `text` fits `width`.
function fitSize(doc, text, width, size, min, style) {
  let s = size;
  doc.setFont('helvetica', style);
  doc.setFontSize(s);
  while (s > min && doc.getTextWidth(text) > width) { s -= 0.5; doc.setFontSize(s); }
  return s;
}
const fill = (doc, c) => doc.setFillColor(c[0], c[1], c[2]);
const stroke = (doc, c) => doc.setDrawColor(c[0], c[1], c[2]);

// Company details: the caller's branding first, the statement's own provider details for any gap.
function brandFrom(data, branding) {
  const out = { ...((data && data.provider) || {}) };
  Object.entries(branding || {}).forEach(([k, v]) => { if (v != null && v !== '') out[k] = v; });
  return out;
}

// ── Sections ──────────────────────────────────────────────────────────────────
function drawLogo(doc, brand, x, y, maxW, maxH) {
  const logo = pdfLogo(brand);
  if (!logo) return null;
  try {
    let { w, h } = logo;
    if (!w || !h) { const p = doc.getImageProperties(logo.dataUrl); w = p.width; h = p.height; }
    if (!w || !h) return null;
    let dh = maxH;
    let dw = (w / h) * dh;
    if (dw > maxW) { dw = maxW; dh = (h / w) * dw; }
    doc.addImage(logo.dataUrl, logo.format, x, y, dw, dh);
    return { w: dw, h: dh };
  } catch { return null; }  // a broken logo must never stop the statement
}

function drawLetterhead(doc, ctx) {
  const { brand } = ctx;
  fill(doc, ctx.accent);
  doc.rect(0, 0, PW, 3.5, 'F');
  const top = 11;
  const rightW = 82;
  let leftBottom = top;
  const logo = drawLogo(doc, brand, M, top, 64, 17);
  if (logo) leftBottom = top + logo.h;
  else if (ctx.company) {
    // No logo: the company name becomes the wordmark, in the brand colour. Large on one line when
    // it fits, smaller on two, and only a very long name is cut (at three lines).
    const maxW = CW - rightW - 8;
    let size = 17;
    let lines = [];
    while (size >= 11) {
      font(doc, size, 'bold', ctx.ink);
      lines = doc.splitTextToSize(ctx.company, maxW);
      if (lines.length === 1 || (size <= 14 && lines.length === 2)) break;
      size -= 1;
    }
    if (size < 11) { size = 11; font(doc, size, 'bold', ctx.ink); lines = wrap(doc, ctx.company, maxW, 3); }
    doc.text(lines, M, top, { baseline: 'top' });
    leftBottom = top + lines.length * lh(size);
  }
  const details = [];
  if (logo && ctx.company) details.push({ text: ctx.company, size: 10, style: 'bold', color: INK });
  const ids = [brand.abn && `ABN ${safe(brand.abn)}`, brand.ndisRegistrationNumber && `NDIS registration ${safe(brand.ndisRegistrationNumber)}`]
    .filter(Boolean).join('   ·   ');
  if (ids) details.push({ text: ids });
  if (brand.address) details.push({ text: safe(brand.address) });
  if (ctx.contact) details.push({ text: ctx.contact });
  if (brand.website) details.push({ text: safe(brand.website) });
  let ry = top + 0.4;
  details.forEach((d) => {
    const size = d.size || 8;
    font(doc, size, d.style || 'normal', d.color || TEXT);
    wrap(doc, d.text, rightW, 2).forEach((ln) => {
      doc.text(ln, PW - M, ry, { align: 'right', baseline: 'top' });
      ry += lh(size);
    });
    if (d.style === 'bold') ry += 0.8;
  });
  const bottom = Math.max(leftBottom, ry) + 5;
  stroke(doc, RULE);
  doc.setLineWidth(0.3);
  doc.line(M, bottom, PW - M, bottom);
  return bottom;
}

function drawTitle(doc, y, ctx) {
  const top = y + 7;
  // Date issued and reference, top right: values in one left-aligned column, labels beside them.
  const meta = [['Date issued', ctx.issued], ['Reference', ctx.reference]].filter((r) => r[1]);
  font(doc, 9, 'bold', INK);
  const valW = Math.max(0, ...meta.map((r) => doc.getTextWidth(r[1])));
  const valX = PW - M - valW;
  font(doc, 7.5, 'normal', MUTED);
  const labelW = Math.max(0, ...meta.map((r) => doc.getTextWidth(r[0])));
  let my = top + 1;
  meta.forEach(([k, v]) => {
    font(doc, 7.5, 'normal', MUTED);
    doc.text(k, valX - 3, my + 0.6, { align: 'right', baseline: 'top' });
    font(doc, 9, 'bold', INK);
    doc.text(v, valX, my, { baseline: 'top' });
    my += 5.4;
  });
  const title = 'Support coordination statement';
  const titleSize = fitSize(doc, title, valX - 3 - labelW - 8 - M, 20, 15, 'bold');
  font(doc, titleSize, 'bold', INK);
  doc.text(title, M, top, { baseline: 'top' });
  font(doc, 10.5, 'normal', TEXT);
  doc.text(ctx.periodLong, M, top + lh(titleSize) + 1.6, { baseline: 'top' });
  return top + 17;
}

function drawParticipant(doc, y, ctx) {
  const pad = 5.5;
  const gap = 6;
  const inner = CW - 2 * pad - 2 * gap;
  const colW = [inner * 0.4, inner * 0.3, inner * 0.3];  // the name gets the widest column
  const colX = [0, colW[0] + gap, colW[0] + colW[1] + 2 * gap];
  const rows = [
    [['Participant', ctx.name, true], ['NDIS number', ctx.ndis], ['Plan dates', ctx.planDates]],
    [['Support level', ctx.level], ['Support coordinator', ctx.coordinator], ['Funding', ctx.funding]],
  ].map((row) => row.map(([label, value, big], ci) => {
    const size = big ? 11.5 : 9.5;
    font(doc, size, big ? 'bold' : 'normal', INK);
    // A person's name is never cut short; other values get two lines.
    return { label, size, big, missing: !value, lines: value ? wrap(doc, value, colW[ci], big ? 4 : 2) : ['Not recorded'] };
  }));
  const labelH = 4.4;
  const rowH = rows.map((r) => labelH + Math.max(...r.map((c) => c.lines.length * lh(c.size))));
  const rowGap = 4;
  const h = pad * 2 + rowH[0] + rowGap + rowH[1] - 1;
  fill(doc, ctx.panelTint);
  doc.roundedRect(M, y, CW, h, 2.5, 2.5, 'F');
  fill(doc, ctx.accent);
  doc.rect(M, y + 4, 1.2, h - 8, 'F');
  let ry = y + pad;
  rows.forEach((r, ri) => {
    r.forEach((c, ci) => {
      const x = M + pad + colX[ci];
      font(doc, 7.5, 'normal', MUTED);
      doc.text(c.label, x, ry, { baseline: 'top' });
      font(doc, c.size, c.big ? 'bold' : 'normal', c.missing ? MUTED : INK);
      doc.text(c.lines, x, ry + labelH, { baseline: 'top' });
    });
    ry += rowH[ri] + rowGap;
  });
  return y + h;
}

function drawCards(doc, y, cards) {
  const gap = 4;
  const w = (CW - gap * (cards.length - 1)) / cards.length;
  const h = 25;
  const inner = w - 8;
  // One size per line type across all cards, so the row reads as a set.
  const labelSize = Math.min(...cards.map((c) => fitSize(doc, c.label, inner, 7.8, 6.5, 'bold')));
  const valueSize = Math.min(15, ...cards.filter((c) => !c.valueSize).map((c) => fitSize(doc, c.value, inner, 15, 9, 'bold')));
  const subSize = Math.min(7.5, ...cards.filter((c) => c.sub).map((c) => fitSize(doc, c.sub, inner, 7.5, 6.3, c.subBold ? 'bold' : 'normal')));
  cards.forEach((c, i) => {
    const x = M + i * (w + gap);
    fill(doc, c.tint || WHITE);
    stroke(doc, c.border || RULE);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, y, w, h, 2, 2, 'FD');
    fill(doc, c.accent);
    doc.roundedRect(x + 4, y + 4, 7, 1.2, 0.6, 0.6, 'F');
    font(doc, labelSize, 'bold', TEXT);
    doc.text(c.label, x + 4, y + 7.6, { baseline: 'top' });
    font(doc, c.valueSize || valueSize, 'bold', c.valueColor || INK);
    doc.text(c.value, x + 4, y + 16.2, { baseline: 'middle' });
    if (c.sub) {
      font(doc, subSize, c.subBold ? 'bold' : 'normal', c.subColor || MUTED);
      doc.text(ellipsize(doc, c.sub, inner), x + 4, y + 20.4, { baseline: 'top' });
    }
  });
  return y + h;
}

function sectionHeading(doc, y, title, note, ctx) {
  fill(doc, ctx.accent);
  doc.roundedRect(M, y + 0.3, 1.4, 4.8, 0.7, 0.7, 'F');
  font(doc, 12, 'bold', INK);
  doc.text(title, M + 4, y, { baseline: 'top' });
  if (note) {
    font(doc, 8, 'normal', MUTED);
    doc.text(note, PW - M, y + 1.2, { align: 'right', baseline: 'top' });
  }
  return y + HEADING_H;
}

function emptyPanel(doc, y, text) {
  fill(doc, PANEL);
  stroke(doc, RULE);
  doc.setLineWidth(0.3);
  doc.roundedRect(M, y, CW, 15, 2, 2, 'FD');
  font(doc, 9, 'normal', TEXT);
  doc.text(text, PW / 2, y + 7.5, { align: 'center', baseline: 'middle' });
  return y + 15;
}

function drawBar(doc, x, y, w, share, status) {
  const h = 2.2;
  fill(doc, RULE);
  doc.roundedRect(x, y, w, h, 1.1, 1.1, 'F');
  const used = Math.max(0, Math.min(1, share));
  if (used > 0) {
    fill(doc, status.bar);
    doc.roundedRect(x, y, Math.max(h, w * used), h, 1.1, 1.1, 'F');
  }
}

function drawPill(doc, x, cy, status) {
  font(doc, 7, 'bold', status.ink);
  const w = doc.getTextWidth(status.word) + 5;
  fill(doc, status.tint);
  doc.roundedRect(x, cy - 2.6, w, 5.2, 2.6, 2.6, 'F');
  doc.text(status.word, x + 2.5, cy, { baseline: 'middle' });
}

const statusOf = (total, remaining) => {
  if (total <= 0) return remaining < 0 ? STATUS.over : STATUS.none;
  if (remaining < 0) return STATUS.over;
  if (remaining === 0) return STATUS.used;
  return remaining / total < LOW_SHARE ? STATUS.low : STATUS.ok;
};
const usedWords = (used, total) => (total > 0 ? `${Math.round((used / total) * 100)}% used` : '');

function tableBase(ctx) {
  return {
    theme: 'plain',
    margin: { left: M, right: M, top: CONT_TOP, bottom: PH - BODY_END },
    rowPageBreak: 'avoid',
    showHead: 'everyPage',
    showFoot: 'lastPage',
    styles: {
      font: 'helvetica', fontSize: 8.5, textColor: TEXT, valign: 'top', overflow: 'linebreak',
      cellPadding: { top: PAD, bottom: PAD, left: PAD_X, right: PAD_X },
      lineColor: RULE, lineWidth: { bottom: 0.25 },
    },
    headStyles: {
      fillColor: ctx.headTint, textColor: ctx.ink, fontStyle: 'bold', fontSize: 7.5, valign: 'middle',
      cellPadding: { top: 2.6, bottom: 2.6, left: PAD_X, right: PAD_X }, lineWidth: 0,
    },
    footStyles: {
      fillColor: ctx.headTint, textColor: ctx.ink, fontStyle: 'bold', fontSize: 9, valign: 'middle',
      cellPadding: { top: 3.2, bottom: 3.2, left: PAD_X, right: PAD_X }, lineColor: ctx.accent, lineWidth: { top: 0.5 },
    },
  };
}
const right = (content, extra) => ({ content, styles: { halign: 'right', ...(extra || {}) } });

// Measure each budget row (name and category wrap inside the first column).
function prepBudgets(doc, ctx) {
  const w = BUDGET_COLS[0] - 2 * PAD_X;
  return ctx.budgets.map((b) => {
    font(doc, 8.5, 'bold');
    const nameLines = wrap(doc, b.name, w, 2);
    font(doc, 7.5, 'normal');
    const catLines = b.category ? wrap(doc, b.category, w, 2) : [];
    const textH = nameLines.length * lh(8.5) + (catLines.length ? 0.3 + catLines.length * lh(7.5) : 0);
    return { ...b, nameLines, catLines, textH, h: Math.max(PAD + textH + PAD + 1, 12) };
  });
}

function drawBudgets(doc, y, ctx, rows) {
  const base = tableBase(ctx);
  const many = rows.length > 1;
  const num = { halign: 'right', cellPadding: { top: PAD, bottom: PAD, left: 1, right: PAD_X } };  // room for six-figure amounts
  autoTable(doc, {
    ...base,
    startY: y,
    head: [['Budget', right('Amount'), right('Used to date'), right('Remaining'), 'Progress', 'Status']],
    body: rows.map((r) => [
      { content: '', styles: { minCellHeight: r.h } },
      money(r.total), money(r.used),
      { content: money(r.remaining), styles: { textColor: r.remaining < 0 ? STATUS.over.ink : INK, fontStyle: 'bold' } },
      '', '',
    ]),
    foot: many ? [[
      'All budgets', right(money(ctx.totals.total), num), right(money(ctx.totals.used), num),
      right(money(ctx.totals.remaining), { ...num, ...(ctx.totals.remaining < 0 ? { textColor: STATUS.over.ink } : {}) }), '', '',
    ]] : [],
    styles: { ...base.styles, valign: 'middle' },
    footStyles: { ...base.footStyles, fontSize: 8.5 },
    columnStyles: Object.fromEntries(BUDGET_COLS.map((w, i) => [i, { cellWidth: w, ...(i >= 1 && i <= 3 ? num : {}) }])),
    didDrawCell: (h) => {
      if (h.section === 'head') return;
      const { cell } = h;
      const isFoot = h.section === 'foot';
      const r = isFoot ? ctx.totals : rows[h.row.index];
      if (!r) return;
      const cy = cell.y + cell.height / 2;
      if (h.column.index === 0 && !isFoot) {
        let ty = cell.y + (cell.height - r.textH) / 2;
        font(doc, 8.5, 'bold', INK);
        doc.text(r.nameLines, cell.x + PAD_X, ty, { baseline: 'top' });
        ty += r.nameLines.length * lh(8.5) + 0.3;
        if (r.catLines.length) { font(doc, 7.5, 'normal', MUTED); doc.text(r.catLines, cell.x + PAD_X, ty, { baseline: 'top' }); }
      }
      if (h.column.index === 4) {
        const pct = usedWords(r.used, r.total);
        if (pct) { font(doc, 7, isFoot ? 'bold' : 'normal', isFoot ? ctx.ink : MUTED); doc.text(pct, cell.x + PAD_X, cy - 1.2, { baseline: 'bottom' }); }
        drawBar(doc, cell.x + PAD_X, cy + 0.4, cell.width - 2 * PAD_X, r.total > 0 ? r.used / r.total : 0, r.status);
      }
      if (h.column.index === 5) drawPill(doc, cell.x + PAD_X, cy, r.status);
    },
  });
  return doc.lastAutoTable.finalY;
}

// Build and measure the supports rows. The month band, the month subtotal and the grand total are
// drawn INSIDE the first/last support rows (rows never split across pages), so none of them can be
// stranded on a page of its own.
function prepSupports(doc, ctx) {
  const body = [];
  const meta = [];
  const several = ctx.months.length > 1;
  ctx.months.forEach((g, gi) => {
    g.lines.forEach((l, i) => {
      const last = i === g.lines.length - 1;
      const sub = last && several ? SUB_H : 0;
      const total = last && gi === ctx.months.length - 1 ? TOTAL_H : 0;
      const w = SUP_COLS[1] - 2 * PAD_X;
      font(doc, 8.5, 'bold');
      const titleLines = wrap(doc, l.title, w, 2);
      font(doc, 7.8, 'normal');
      const noteLines = l.note ? wrap(doc, l.note, w, 6) : [];
      const top = PAD + (i === 0 ? BAND : 0);
      const bottom = PAD + 0.4 + sub + total;
      const textH = titleLines.length * lh(8.5) + (noteLines.length ? 0.5 + noteLines.length * lh(7.8) : 0);
      const pad = { top, bottom, left: PAD_X, right: PAD_X };
      body.push([
        { content: l.dateText, styles: { cellPadding: pad, textColor: INK } },
        { content: '', styles: { cellPadding: pad, minCellHeight: top + textH + bottom } },
        { content: l.time, styles: { cellPadding: pad, fontSize: 8 } },
        { content: l.hours == null ? '' : num2(l.hours), styles: { cellPadding: pad, halign: 'right' } },
        { content: money(l.amount), styles: { cellPadding: { ...pad, left: 1 }, halign: 'right', textColor: INK } },
      ]);
      meta.push({ l, titleLines, noteLines, top, month: i === 0 ? g : null, sub: sub ? g : null, total: total > 0, h: top + textH + bottom });
    });
  });
  return { body, meta, firstH: meta.length ? meta[0].h : 0 };
}

function drawSupports(doc, y, ctx, prep) {
  const base = tableBase(ctx);
  const colX = SUP_COLS.map((w, i) => M + SUP_COLS.slice(0, i).reduce((s, v) => s + v, 0));
  const { body, meta } = prep;
  autoTable(doc, {
    ...base,
    startY: y,
    head: [['Date', 'What was done', 'Time', right('Hours'), right('Amount')]],
    body,
    columnStyles: Object.fromEntries(SUP_COLS.map((w, i) => [i, { cellWidth: w, ...(i >= 3 ? { halign: 'right' } : {}) }])),
    didDrawCell: (h) => {
      if (h.section !== 'body') return;
      const m = meta[h.row.index];
      if (!m) return;
      const { cell } = h;
      if (h.column.index === 0) {
        const bottomY = cell.y + cell.height;
        if (m.month) {
          const g = m.month;
          const bandH = BAND - 1.6;
          fill(doc, ctx.bandTint);
          doc.rect(M, cell.y + 0.8, CW, bandH, 'F');
          fill(doc, ctx.accent);
          doc.rect(M, cell.y + 0.8, 1, bandH, 'F');
          const mid = cell.y + 0.8 + bandH / 2;
          font(doc, 9, 'bold', ctx.ink);
          doc.text(g.label, M + PAD_X + 1, mid, { baseline: 'middle' });
          font(doc, 7.5, 'normal', TEXT);
          doc.text(`${plural(g.lines.length, 'support', 'supports')}   ·   ${hoursWords(g.hours)}   ·   ${money(g.amount)}`,
            PW - M - PAD_X, mid, { align: 'right', baseline: 'middle' });
        }
        if (m.sub) {
          const sy = bottomY - (m.total ? TOTAL_H : 0) - SUB_H;
          const mid = sy + SUB_H / 2;
          stroke(doc, RULE);
          doc.setLineWidth(0.25);
          doc.line(colX[2], sy, PW - M, sy);
          font(doc, 7.8, 'bold', MUTED);
          doc.text(`Subtotal for ${m.sub.label}`, colX[3] - PAD_X, mid, { align: 'right', baseline: 'middle' });
          font(doc, 8.5, 'bold', INK);
          doc.text(num2(m.sub.hours), colX[4] - PAD_X, mid, { align: 'right', baseline: 'middle' });
          doc.text(money(m.sub.amount), PW - M - PAD_X, mid, { align: 'right', baseline: 'middle' });
        }
        if (m.total) {
          const ty = bottomY - TOTAL_H + 1.4;
          const th = TOTAL_H - 1.4;
          const mid = ty + th / 2;
          fill(doc, ctx.headTint);
          doc.rect(M, ty, CW, th, 'F');
          stroke(doc, ctx.accent);
          doc.setLineWidth(0.5);
          doc.line(M, ty, PW - M, ty);
          font(doc, 9, 'bold', ctx.ink);
          doc.text(`Total for this period   ·   ${plural(ctx.lines.length, 'support', 'supports')}`, M + PAD_X, mid, { baseline: 'middle' });
          doc.text(num2(ctx.hours), colX[4] - PAD_X, mid, { align: 'right', baseline: 'middle' });
          doc.text(money(ctx.total), PW - M - PAD_X, mid, { align: 'right', baseline: 'middle' });
        }
      }
      if (h.column.index === 1) {
        const x = cell.x + PAD_X;
        let ty = cell.y + m.top;
        font(doc, 8.5, 'bold', INK);
        doc.text(m.titleLines, x, ty, { baseline: 'top' });
        ty += m.titleLines.length * lh(8.5) + 0.5;
        if (m.noteLines.length) {
          font(doc, 7.8, 'normal', m.l.noteMuted ? MUTED : TEXT);
          doc.text(m.noteLines, x, ty, { baseline: 'top' });
        }
      }
    },
  });
  return doc.lastAutoTable.finalY;
}

function drawAbout(doc, y, ctx) {
  const notes = [
    ctx.hasBudgets && `"Used to date" is everything delivered or committed against a budget up to ${ctx.issued}, including support not yet claimed and provider quotes you have accepted.`,
    ctx.hasBudgets && 'A budget is "running low" when less than 15% of it is left.',
    'Times and hours are as recorded by your support coordinator. Amounts are in Australian dollars.',
  ].filter(Boolean);
  font(doc, 7.6, 'normal', TEXT);
  const wrapped = notes.map((n) => doc.splitTextToSize(n, CW - 4));
  const h = 6 + wrapped.reduce((s, l) => s + l.length * lh(7.6) + 1, 0);
  let ty = y;
  if (ty + h > BODY_END) { doc.addPage(); ty = CONT_TOP; }
  font(doc, 8.5, 'bold', INK);
  doc.text('About this statement', M, ty, { baseline: 'top' });
  ty += 5.4;
  wrapped.forEach((lines) => {
    fill(doc, ctx.accent);
    doc.circle(M + 0.8, ty + 1.45, 0.55, 'F');
    font(doc, 7.6, 'normal', TEXT);
    doc.text(lines, M + 4, ty, { baseline: 'top' });
    ty += lines.length * lh(7.6) + 1;
  });
  return ty;
}

function drawRunningHeader(doc, ctx) {
  fill(doc, ctx.accent);
  doc.rect(0, 0, PW, 3.5, 'F');
  const periodText = ctx.periodShort;
  font(doc, 8, 'normal', MUTED);
  const periodW = doc.getTextWidth(periodText);
  font(doc, 8.5, 'bold', INK);
  const title = 'Support coordination statement';
  doc.text(title, M, 10, { baseline: 'top' });
  const tw = doc.getTextWidth(title);
  font(doc, 8.5, 'normal', TEXT);
  doc.text(ellipsize(doc, `   ·   ${ctx.name}`, CW - tw - periodW - 6), M + tw, 10, { baseline: 'top' });
  font(doc, 8, 'normal', MUTED);
  doc.text(periodText, PW - M, 10.3, { align: 'right', baseline: 'top' });
  stroke(doc, RULE);
  doc.setLineWidth(0.3);
  doc.line(M, 16.5, PW - M, 16.5);
}

function drawFooter(doc, page, pages, ctx) {
  stroke(doc, RULE);
  doc.setLineWidth(0.3);
  doc.line(M, FOOT_RULE, PW - M, FOOT_RULE);
  const pageText = `Page ${page} of ${pages}`;
  font(doc, 7.5, 'bold', INK);
  const pw = doc.getTextWidth(pageText);
  doc.text(pageText, PW - M, FOOT_RULE + 3.4, { align: 'right', baseline: 'top' });
  font(doc, 7.5, 'normal', TEXT);
  const room = CW - pw - 8;
  const help = ctx.helpLines.find((t) => doc.getTextWidth(t) <= room) || ellipsize(doc, ctx.helpLines[ctx.helpLines.length - 1], room);
  doc.text(help, M, FOOT_RULE + 3.4, { baseline: 'top' });
  let x = M;
  if (ctx.company) {
    font(doc, 7, 'bold', MUTED);
    const name = ellipsize(doc, ctx.company, CW * 0.5);
    doc.text(name, x, FOOT_RULE + 7.6, { baseline: 'top' });
    x += doc.getTextWidth(name);
  }
  const rest = ctx.contact ? `${ctx.company ? '   ·   ' : ''}${ctx.contact}` : '';
  if (rest) {
    font(doc, 7, 'normal', MUTED);
    doc.text(ellipsize(doc, rest, PW - M - x), x, FOOT_RULE + 7.6, { baseline: 'top' });
  }
}

// ── Main ──────────────────────────────────────────────────────────────────────
export function generateScStatementPdf(data, branding) {
  const d = data || {};
  const client = d.client || {};
  const eng = d.engagement || {};
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // Colour: the company's brand colour leads; Support Coordination violet when it has none.
  const brand = brandFrom(d, branding);
  const rgb = brandRgb(brand, VIOLET);
  const accent = [rgb.r, rgb.g, rgb.b];
  const issuedIso = todayIso();

  // Who and when.
  const name = safe(`${client.first_name || ''} ${client.last_name || ''}`) || 'Participant';
  const period = range(d.from, d.to);
  const plan = range(eng.plan_start, eng.plan_end);
  const refMonth = (parseDay(d.to) ? String(d.to) : issuedIso).slice(0, 7).replace('-', '');
  const coordinator = safe(eng.coordinator_name);

  // Budgets: each with its status; the headline uses the support coordination budget(s) when the
  // plan's budgets name one, otherwise all of them.
  const budgets = (d.budgets || []).map((b) => {
    const total = Number(b.budget_amount) || 0;
    const used = Number(b.committed) || 0;
    const remaining = b.remaining == null || b.remaining === '' ? total - used : Number(b.remaining) || 0;
    const bname = safe(b.budget_name || b.support_category) || 'Budget';
    const category = safe(b.support_category);
    return {
      name: bname, category: category && category !== bname ? category : '', total, used, remaining,
      status: statusOf(total, remaining), isSc: /coordinat/i.test(`${b.budget_name || ''} ${b.support_category || ''}`),
    };
  });
  const scBudgets = budgets.filter((b) => b.isSc);
  const headline = scBudgets.length ? scBudgets : budgets;
  const totalsOf = (list) => {
    const t = { total: sum(list, 'total'), used: sum(list, 'used'), remaining: sum(list, 'remaining') };
    return { ...t, status: statusOf(t.total, t.remaining) };
  };

  // Supports delivered, grouped by month.
  const lines = (d.lines || []).map((l) => {
    const summary = clip(safe(l.summary), NOTE_MAX);
    let title = safe(l.activity_name) || CLAIM_WORDS[String(l.claim_type || '').toUpperCase()]
      || CONTACT_WORDS[String(l.contact_type || '').toLowerCase()] || safe(l.support_item_name);
    let note = summary;
    let noteMuted = false;
    if (!title) {
      title = 'Support coordination';
      // A code only when there is nothing else to say about the support.
      if (!note && l.support_item_code) { note = `NDIS support item ${safe(l.support_item_code)}`; noteMuted = true; }
    }
    let hours = null;
    if (l.duration_minutes != null && l.duration_minutes !== '' && Number.isFinite(Number(l.duration_minutes))) hours = Number(l.duration_minutes) / 60;
    else if (l.qty != null && l.qty !== '' && (!l.unit || /hour/i.test(String(l.unit)))) hours = Number(l.qty) || 0;
    return {
      date: String(l.note_date || '').slice(0, 10), dateText: weekdayDay(l.note_date) || 'No date', title, note, noteMuted,
      time: timeRange(l.start_time, l.end_time), hours, amount: Number(l.line_total) || 0,
    };
  });
  const months = [];
  lines.forEach((l) => {
    const key = monthKey(l.date);
    let g = months.find((x) => x.key === key);
    if (!g) { g = { key, label: monthLabel(key), lines: [] }; months.push(g); }
    g.lines.push(l);
  });
  months.forEach((g) => { g.hours = sum(g.lines, 'hours'); g.amount = sum(g.lines, 'amount'); });
  const hours = sum(lines, 'hours');
  const total = d.total != null && d.total !== '' ? Number(d.total) || 0 : sum(lines, 'amount');

  const company = safe(brand.name);
  const contact = [brand.phone, brand.email].map(safe).filter(Boolean).join('   ·   ');
  const ink = readable(accent);
  const ctx = {
    brand, accent, ink, company, contact, name,
    headTint: mix(accent, WHITE, 0.88), bandTint: mix(accent, WHITE, 0.93), panelTint: mix(accent, WHITE, 0.95),
    periodLong: period ? period.long : 'All supports to date', periodShort: period ? period.short : 'All supports to date',
    issued: dayLong(issuedIso),
    reference: eng.id ? `SC-${refMonth}-${String(eng.id).replace(/-/g, '').slice(0, 6).toUpperCase()}` : '',
    ndis: safe(client.ndis_number), planDates: plan ? plan.short : '', coordinator,
    level: LEVELS[eng.level] || sentence(safe(String(eng.level || '').replace(/_/g, ' '))),
    funding: fundingWords(eng.funding_source),
    budgets, totals: totalsOf(budgets), hasBudgets: budgets.length > 0,
    lines, months, hours, total,
    // Longest first; the footer uses the first that fits beside "Page X of Y".
    helpLines: [
      company && `Questions about this statement? Contact your support coordinator${coordinator ? `, ${coordinator}` : ''}, at ${company}.`,
      `Questions about this statement? Contact your support coordinator${coordinator ? `, ${coordinator}` : ''}.`,
    ].filter(Boolean),
  };

  // ── Page 1 ──
  let y = drawLetterhead(doc, ctx);
  y = drawTitle(doc, y, ctx);
  y = drawParticipant(doc, y + 2, ctx);

  // Headline figures.
  const supportsWord = lines.length ? `${hoursWords(hours)}  ·  ${plural(lines.length, 'support', 'supports')}` : 'No supports recorded';
  let cards;
  if (budgets.length) {
    const t = totalsOf(headline);
    let headlineLabel = 'Budget';
    if (scBudgets.length) headlineLabel = 'Support coordination budget';
    else if (budgets.length > 1) headlineLabel = 'Total budget';
    cards = [
      { label: headlineLabel, value: money(t.total), sub: headline.length > 1 ? `${headline.length} budgets` : 'For this plan', accent },
      { label: 'Used this period', value: money(total), sub: supportsWord, accent },
      { label: 'Used to date', value: money(t.used), sub: t.total > 0 ? `${Math.round((t.used / t.total) * 100)}% of the budget` : '', accent },
      {
        label: 'Remaining', value: money(t.remaining), sub: t.status.word, subBold: true, subColor: t.status.ink,
        accent: t.status.bar, tint: t.status.tint, border: mix(t.status.bar, WHITE, 0.6), valueColor: t.status.ink,
      },
    ];
  } else {
    cards = [
      { label: 'Used this period', value: money(total), sub: lines.length ? 'Support coordination delivered' : 'No supports recorded', accent },
      { label: 'Hours this period', value: round2(hours).toLocaleString('en-AU', { maximumFractionDigits: 2 }), sub: 'Hours of support', accent },
      { label: 'Supports delivered', value: lines.length.toLocaleString('en-AU'), sub: 'In this period', accent },
      { label: 'Budget', value: 'Not recorded', valueSize: 11, valueColor: MUTED, sub: 'Ask your support coordinator', accent: RULE, tint: PANEL },
    ];
  }
  y = drawCards(doc, y + 6, cards);

  // Each section heading stays with its table's column headings and first row.
  const keep = (need) => { if (y + need > BODY_END) { doc.addPage(); y = CONT_TOP; } };

  // Budget position.
  y += 9;
  const budgetRows = prepBudgets(doc, ctx);
  keep(HEADING_H + (budgetRows.length ? HEAD_H + budgetRows[0].h : 15));
  y = sectionHeading(doc, y, 'Your budget position', budgets.length ? `As at ${ctx.issued}` : '', ctx);
  if (budgetRows.length) y = drawBudgets(doc, y, ctx, budgetRows);
  else y = emptyPanel(doc, y, 'No budget has been recorded for this plan yet. Your support coordinator can add it.');

  // Supports delivered.
  y += 9;
  const supports = prepSupports(doc, ctx);
  keep(HEADING_H + (lines.length ? HEAD_H + supports.firstH : 15));
  const deliveredNote = lines.length ? `${plural(lines.length, 'support', 'supports')}  ·  ${hoursWords(hours)}  ·  ${money(total)}` : '';
  y = sectionHeading(doc, y, 'Supports delivered', deliveredNote, ctx);
  if (lines.length) y = drawSupports(doc, y, ctx, supports);
  else y = emptyPanel(doc, y, period ? `No support coordination was recorded in this period (${period.long}).` : 'No support coordination has been recorded yet.');

  drawAbout(doc, y + 8, ctx);

  // Running header (pages 2+) and footer (every page), now the page count is known.
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    if (i > 1) drawRunningHeader(doc, ctx);
    drawFooter(doc, i, pages, ctx);
  }

  const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'participant';
  doc.save(`sc-statement-${slug}-${(parseDay(d.to) ? String(d.to) : issuedIso).slice(0, 10)}.pdf`);
}
